import { useState, useEffect } from "react";
import { clientsApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, Search, MoreVertical, Users, Trash2, Loader2, FileText, CreditCard, Download } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REGIMEN_FISCAL = [
  { value: "601", label: "601 - General de Ley Personas Morales" },
  { value: "603", label: "603 - Personas Morales con Fines no Lucrativos" },
  { value: "605", label: "605 - Sueldos y Salarios" },
  { value: "606", label: "606 - Arrendamiento" },
  { value: "607", label: "607 - Régimen de Enajenación o Adquisición de Bienes" },
  { value: "608", label: "608 - Demás ingresos" },
  { value: "610", label: "610 - Residentes en el Extranjero" },
  { value: "611", label: "611 - Ingresos por Dividendos" },
  { value: "612", label: "612 - Personas Físicas con Actividades Empresariales" },
  { value: "614", label: "614 - Ingresos por intereses" },
  { value: "616", label: "616 - Sin obligaciones fiscales" },
  { value: "620", label: "620 - Sociedades Cooperativas de Producción" },
  { value: "621", label: "621 - Incorporación Fiscal" },
  { value: "622", label: "622 - Actividades Agrícolas, Ganaderas, Silvícolas" },
  { value: "623", label: "623 - Opcional para Grupos de Sociedades" },
  { value: "624", label: "624 - Coordinados" },
  { value: "625", label: "625 - Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas" },
  { value: "626", label: "626 - Régimen Simplificado de Confianza" }
];

const USO_CFDI = [
  { value: "G01", label: "G01 - Adquisición de mercancías" },
  { value: "G02", label: "G02 - Devoluciones, descuentos o bonificaciones" },
  { value: "G03", label: "G03 - Gastos en general" },
  { value: "I01", label: "I01 - Construcciones" },
  { value: "I02", label: "I02 - Mobiliario y equipo de oficina" },
  { value: "I03", label: "I03 - Equipo de transporte" },
  { value: "I04", label: "I04 - Equipo de computo y accesorios" },
  { value: "I08", label: "I08 - Otra maquinaria y equipo" },
  { value: "D01", label: "D01 - Honorarios médicos, dentales y gastos hospitalarios" },
  { value: "D02", label: "D02 - Gastos médicos por incapacidad o discapacidad" },
  { value: "D03", label: "D03 - Gastos funerales" },
  { value: "D04", label: "D04 - Donativos" },
  { value: "D05", label: "D05 - Intereses reales efectivamente pagados por créditos hipotecarios" },
  { value: "D06", label: "D06 - Aportaciones voluntarias al SAR" },
  { value: "D07", label: "D07 - Primas por seguros de gastos médicos" },
  { value: "D08", label: "D08 - Gastos de transportación escolar obligatoria" },
  { value: "D09", label: "D09 - Depósitos en cuentas para el ahorro, primas de pensiones" },
  { value: "D10", label: "D10 - Pagos por servicios educativos (colegiaturas)" },
  { value: "S01", label: "S01 - Sin efectos fiscales" },
  { value: "CP01", label: "CP01 - Pagos" }
];

const ClientsPage = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [statementDialog, setStatementDialog] = useState({ open: false, client: null, data: null, loading: false });
  
  const [formData, setFormData] = useState({
    name: "",
    rfc: "",
    razon_social: "",
    regimen_fiscal: "",
    codigo_postal: "",
    email: "",
    phone: "",
    uso_cfdi: "G03",
    has_credit: false,
    credit_limit: "",
    credit_days: ""
  });

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const response = await clientsApi.list();
      setClients(response.data);
    } catch (error) {
      toast.error("Error al cargar clientes");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.rfc || formData.rfc.length < 12) {
      toast.error("RFC inválido");
      return;
    }

    setSubmitting(true);

    try {
      await clientsApi.create({
        ...formData,
        credit_limit: formData.has_credit && formData.credit_limit ? parseFloat(formData.credit_limit) : null,
        credit_days: formData.has_credit && formData.credit_days ? parseInt(formData.credit_days) : null
      });
      toast.success("Cliente creado exitosamente");
      setDialogOpen(false);
      resetForm();
      loadClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear cliente");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm("¿Estás seguro de eliminar este cliente?")) return;

    try {
      await clientsApi.delete(clientId);
      toast.success("Cliente eliminado");
      loadClients();
    } catch (error) {
      toast.error("Error al eliminar cliente");
    }
  };

  const handleViewStatement = async (client) => {
    setStatementDialog({ open: true, client, data: null, loading: true });
    
    try {
      const token = localStorage.getItem("cia_token");
      const response = await axios.get(
        `${API}/clients/${client.id}/statement`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStatementDialog(prev => ({ ...prev, data: response.data, loading: false }));
    } catch (error) {
      toast.error("Error al cargar estado de cuenta");
      setStatementDialog(prev => ({ ...prev, loading: false }));
    }
  };

  const handleDownloadStatementPdf = (clientId) => {
    window.open(`${API}/clients/${clientId}/statement/pdf`, "_blank");
  };

  const resetForm = () => {
    setFormData({
      name: "",
      rfc: "",
      razon_social: "",
      regimen_fiscal: "",
      codigo_postal: "",
      email: "",
      phone: "",
      uso_cfdi: "G03",
      has_credit: false,
      credit_limit: "",
      credit_days: ""
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.rfc.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5B9BD5]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="page-title">Clientes</h1>
          <p className="page-subtitle">Administra tu base de datos de clientes</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="cia-btn-primary w-full sm:w-auto" data-testid="new-client-btn">
              <Plus className="w-5 h-5 mr-2" />
              Nuevo Cliente
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto mx-2 sm:mx-auto">
            <DialogHeader>
              <DialogTitle>Agregar Nuevo Cliente</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-2">
                  <Label>Nombre / Alias</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Nombre del cliente"
                    className="cia-input"
                    required
                    data-testid="client-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>RFC</Label>
                  <Input
                    value={formData.rfc}
                    onChange={(e) => setFormData({ ...formData, rfc: e.target.value.toUpperCase() })}
                    placeholder="XAXX010101000"
                    className="cia-input font-mono"
                    required
                    maxLength={13}
                    data-testid="client-rfc-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Razón Social</Label>
                <Input
                  value={formData.razon_social}
                  onChange={(e) => setFormData({ ...formData, razon_social: e.target.value })}
                  placeholder="Razón social completa"
                  className="cia-input"
                  required
                  data-testid="client-razon-social-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Régimen Fiscal</Label>
                  <Select 
                    value={formData.regimen_fiscal} 
                    onValueChange={(value) => setFormData({ ...formData, regimen_fiscal: value })}
                  >
                    <SelectTrigger data-testid="client-regimen-select">
                      <SelectValue placeholder="Seleccionar régimen" />
                    </SelectTrigger>
                    <SelectContent>
                      {REGIMEN_FISCAL.map((regimen) => (
                        <SelectItem key={regimen.value} value={regimen.value}>{regimen.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Código Postal</Label>
                  <Input
                    value={formData.codigo_postal}
                    onChange={(e) => setFormData({ ...formData, codigo_postal: e.target.value })}
                    placeholder="00000"
                    className="cia-input"
                    required
                    maxLength={5}
                    data-testid="client-cp-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Correo Electrónico</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="cliente@empresa.com"
                    className="cia-input"
                    required
                    data-testid="client-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Teléfono (opcional)</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+52 55 1234 5678"
                    className="cia-input"
                    data-testid="client-phone-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Uso CFDI</Label>
                <Select 
                  value={formData.uso_cfdi} 
                  onValueChange={(value) => setFormData({ ...formData, uso_cfdi: value })}
                >
                  <SelectTrigger data-testid="client-uso-cfdi-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {USO_CFDI.map((uso) => (
                      <SelectItem key={uso.value} value={uso.value}>{uso.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Credit Settings */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <Label className="text-base font-medium">Crédito</Label>
                    <p className="text-sm text-slate-500">¿Este cliente tiene crédito?</p>
                  </div>
                  <Switch
                    checked={formData.has_credit}
                    onCheckedChange={(checked) => setFormData({ ...formData, has_credit: checked })}
                    data-testid="client-has-credit-switch"
                  />
                </div>
                
                {formData.has_credit && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                    <div className="space-y-2">
                      <Label>Límite de Crédito (MXN)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.credit_limit}
                        onChange={(e) => setFormData({ ...formData, credit_limit: e.target.value })}
                        placeholder="50000.00"
                        className="cia-input"
                        data-testid="client-credit-limit-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Días de Crédito</Label>
                      <Input
                        type="number"
                        value={formData.credit_days}
                        onChange={(e) => setFormData({ ...formData, credit_days: e.target.value })}
                        placeholder="30"
                        className="cia-input"
                        data-testid="client-credit-days-input"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="cia-btn-primary" disabled={submitting} data-testid="save-client-btn">
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Guardar Cliente"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <Card className="cia-card">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Buscar por nombre, RFC o email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 cia-input"
              data-testid="search-clients-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Clients table */}
      <Card className="cia-card">
        <CardContent className="p-0">
          {filteredClients.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No hay clientes</h3>
              <p className="text-slate-500 mb-6">
                {searchTerm 
                  ? "No se encontraron clientes con ese criterio"
                  : "Agrega tu primer cliente para comenzar a facturar"}
              </p>
              {!searchTerm && (
                <Button className="cia-btn-primary" onClick={() => setDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar Cliente
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto -mx-3 sm:mx-0">
              <table className="data-table min-w-[500px] sm:min-w-full">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th className="hidden sm:table-cell">RFC</th>
                    <th className="hidden md:table-cell">Email</th>
                    <th className="hidden lg:table-cell">Régimen</th>
                    <th>Crédito</th>
                    <th className="text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClients.map((client) => (
                    <tr key={client.id} data-testid={`client-row-${client.id}`}>
                      <td className="font-medium text-xs sm:text-sm max-w-[150px] truncate">{client.name}</td>
                      <td className="font-mono text-xs hidden sm:table-cell">{client.rfc}</td>
                      <td className="text-xs hidden md:table-cell max-w-[150px] truncate">{client.email}</td>
                      <td className="hidden lg:table-cell text-xs">{client.regimen_fiscal}</td>
                      <td>
                        {client.has_credit ? (
                          <span className="inline-flex items-center gap-1 text-green-700 bg-green-50 px-2 py-1 rounded-full text-xs font-medium">
                            <CreditCard className="w-3 h-3" />
                            {client.credit_days ? `${client.credit_days}d` : 'Sí'}
                          </span>
                        ) : (
                          <span className="text-slate-400 text-xs">No</span>
                        )}
                      </td>
                      <td className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem onClick={() => handleViewStatement(client)}>
                              <FileText className="w-4 h-4 mr-2" />
                              Estado de Cuenta
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => handleDelete(client.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Eliminar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Statement Dialog */}
      <Dialog open={statementDialog.open} onOpenChange={(open) => setStatementDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Estado de Cuenta - {statementDialog.client?.name}
              </div>
              {statementDialog.data && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleDownloadStatementPdf(statementDialog.client?.id)}
                  data-testid="download-statement-pdf-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Descargar PDF
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {statementDialog.loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#5B9BD5]" />
            </div>
          ) : statementDialog.data ? (
            <div className="space-y-6 mt-4">
              {/* Client Info & Summary */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="border-slate-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-slate-500">Información del Cliente</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-1 text-sm">
                    <p><span className="font-medium">RFC:</span> {statementDialog.data.client.rfc}</p>
                    <p><span className="font-medium">Email:</span> {statementDialog.data.client.email}</p>
                    {statementDialog.data.client.has_credit && (
                      <>
                        <p><span className="font-medium">Límite de Crédito:</span> {formatCurrency(statementDialog.data.client.credit_limit || 0)}</p>
                        <p><span className="font-medium">Días de Crédito:</span> {statementDialog.data.client.credit_days || '-'}</p>
                      </>
                    )}
                  </CardContent>
                </Card>
                
                <Card className="border-slate-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-slate-500">Resumen de Cuenta</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-xl font-bold text-slate-900">{formatCurrency(statementDialog.data.summary.total_invoiced)}</p>
                        <p className="text-xs text-slate-500">Total Facturado</p>
                      </div>
                      <div>
                        <p className="text-xl font-bold text-green-600">{formatCurrency(statementDialog.data.summary.total_paid)}</p>
                        <p className="text-xs text-slate-500">Total Pagado</p>
                      </div>
                      <div>
                        <p className="text-xl font-bold text-red-600">{formatCurrency(statementDialog.data.summary.total_balance)}</p>
                        <p className="text-xs text-slate-500">Saldo Pendiente</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Statement Items */}
              {statementDialog.data.items.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  No hay facturas registradas para este cliente
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Folio</th>
                        <th>Fecha</th>
                        <th>Total</th>
                        <th>Pagos</th>
                        <th>Notas Cred.</th>
                        <th>Saldo</th>
                        <th>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statementDialog.data.items.map((item) => (
                        <tr key={item.id}>
                          <td className="font-medium">{item.folio}</td>
                          <td className="text-slate-500">
                            {new Date(item.date).toLocaleDateString('es-MX')}
                          </td>
                          <td>{formatCurrency(item.total)}</td>
                          <td className="text-green-600">{formatCurrency(item.payments)}</td>
                          <td className="text-orange-600">{formatCurrency(item.credits)}</td>
                          <td className={item.balance > 0 ? 'text-red-600 font-medium' : 'text-green-600'}>
                            {formatCurrency(item.balance)}
                          </td>
                          <td>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              item.status === 'issued' ? 'bg-blue-50 text-blue-700' : 
                              item.status === 'cancellation_pending' ? 'bg-orange-50 text-orange-700' : 
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {item.status === 'issued' ? 'Timbrada' : 
                               item.status === 'cancellation_pending' ? 'Cancel. Pend.' : item.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientsPage;
